import { Controller } from "@hotwired/stimulus"
import { apiGet, apiPost } from "../shared/api"
import { escapeHtml, formatDate, loadTranslations, t } from "../shared/utils"

export default class extends Controller {
  static values = {
    announcementId: String,
    userSignedIn: { type: Boolean, default: false }
  }

  connect() {
    loadTranslations()
    this._load()
  }

  async _load() {
    try {
      const json = await apiGet(`/api/v1/partner_announcements/${this.announcementIdValue}`)
      this.ann = json.data
      this._render()
    } catch (error) {
      console.error("Error loading announcement:", error)
      this.element.innerHTML = `<div class="alert alert-danger">${escapeHtml(error.message)}</div>`
    }
  }

  _render() {
    const a = this.ann
    const perms = a.permissions || {}
    const hasGallery = a.media && a.media.length > 0
    const galleryHtml = hasGallery ? this._renderGallery(a) : ""
    const contentColClass = hasGallery ? "col-md-7" : "col-12"

    this.element.innerHTML = `
      <div class="row">
        <div class="col-lg-8">
          <div class="card mb-4"><div class="row g-0">
            ${galleryHtml}
            <div class="${contentColClass}"><div class="card-body">
              <div class="d-flex justify-content-between align-items-start gap-2">
                <h3 class="card-title mb-3">${escapeHtml(a.title)}</h3>
                <a class="forum-share-link text-muted" href="#" data-action="click->partner-announcement-show#openShareModal" title="${escapeHtml(t("share", { default: "Udostępnij" }))}"><i class="fa-solid fa-share-nodes"></i></a>
              </div>
              <div class="partner-announcement-attributes mb-2">
                ${a.club_name ? this._attrRow(t("club_label", { default: "Klub" }), a.club_slug ? `<a href="/clubs/${escapeHtml(a.club_slug)}" class="text-decoration-none">${escapeHtml(a.club_name)}</a>` : escapeHtml(a.club_name), true) : ""}
                ${this._stylesRow(a.dance_styles)}
                ${this._ageRangeRow(a.looking_for)}
                ${this._heightRangeRow(a.looking_for)}
                ${a.competition_format ? this._attrRow(t("competition_format"), a.competition_format) : ""}
                ${a.age_categories?.length ? this._attrRow(t("age_category"), a.age_categories.join(", ")) : ""}
                ${a.tags?.length ? this._tagsRow(a.tags) : ""}
                ${this._extraInfoRow(a)}
              </div>
              <hr class="my-3">
              <h6 class="mb-2">${escapeHtml(t("description_label", { default: "Opis" }))}</h6>
              <p class="card-text">${this._simpleFormat(a.description || "-")}</p>
            </div></div>
          </div></div>
        </div>
        <div class="col-lg-4">
          <div class="card"><div class="card-body">
            <h5 class="mb-2">${escapeHtml(a.user?.name)}</h5>
            ${this._locationHtml(a.location)}
            ${this._datesHtml(a)}
            <hr>
            <h6>${escapeHtml(t("contact", { default: "Kontakt" }))}</h6>
            ${this._contactHtml(a.contact)}
            ${this._videoLinkHtml(a)}
          </div></div>
          ${perms.can_report ? this._reportLinkHtml() : ""}
        </div>
      </div>
      <div class="mt-4 d-flex flex-wrap gap-2 align-items-center">
        <a href="/search" class="default-btn"><i class="fa-solid fa-arrow-left me-2"></i>${escapeHtml(t("back", { default: "Wróć" }))}</a>
        ${perms.can_edit ? `<a href="/partner_announcements/${a.id}/edit" class="default-btn"><i class="fa-solid fa-pen me-2"></i>${escapeHtml(t("common.edit", { default: "Edytuj" }))}</a>` : ""}
        ${perms.can_publish ? `<button class="default-btn cta-primary" data-action="click->partner-announcement-show#publish">${escapeHtml(t("publish", { default: "Opublikuj" }))}</button>` : ""}
        ${perms.can_close ? `<button class="default-btn" data-action="click->partner-announcement-show#close">${escapeHtml(t("close_btn", { default: "Zamknij" }))}</button>` : ""}
        ${perms.can_reopen ? `<button class="default-btn cta-primary" data-action="click->partner-announcement-show#reopen">${escapeHtml(t("reopen", { default: "Wznów" }))}</button>` : ""}
      </div>
      ${this._shareModalHtml()}
      ${perms.can_report ? this._reportModalHtml() : ""}`

    this._initShareModal()
    this._initLightcase()
  }

  _attrRow(label, value, isHtml = false) {
    return `<div class="mb-1"><span class="fw-semibold me-1">${escapeHtml(label)}:</span>${isHtml ? value : escapeHtml(value)}</div>`
  }

  _stylesRow(styles) {
    if (!styles || styles.length === 0) return this._attrRow(t("dance_styles"), "-")
    const display = styles.map(s => s.level_code ? `${s.name} (${s.level_code})` : s.name).join(", ")
    return this._attrRow(t("dance_styles"), display)
  }

  _ageRangeRow(lf) {
    if (!lf || (!lf.age_min && !lf.age_max)) return ""
    let val
    if (lf.age_min && lf.age_max) val = t("age_from_to", { min: lf.age_min, max: lf.age_max })
    else if (lf.age_min) val = t("age_from", { age: lf.age_min })
    else val = t("age_to", { age: lf.age_max })
    return this._attrRow(t("age_range"), val)
  }

  _heightRangeRow(lf) {
    if (!lf || (!lf.height_min && !lf.height_max)) return ""
    let val
    if (lf.height_min && lf.height_max) val = t("height_from_to", { min: lf.height_min, max: lf.height_max })
    else if (lf.height_min) val = t("height_from", { height: lf.height_min })
    else val = t("height_to", { height: lf.height_max })
    return this._attrRow(t("height_range"), val)
  }

  _tagsRow(tags) {
    const badges = tags.map(tag => `<span class="badge bg-secondary">${escapeHtml(tag)}</span>`).join("")
    return `<div class="mb-2"><span class="fw-semibold me-1">${escapeHtml(t("goal_tags"))}:</span><div class="d-flex flex-wrap gap-1 mt-1">${badges}</div></div>`
  }

  _extraInfoRow(a) {
    const opts = []
    if (a.willing_to_commute) opts.push(t("willing_to_commute"))
    if (a.willing_to_relocate) opts.push(t("willing_to_relocate"))
    if (a.considering_club_change) opts.push(t("considering_club_change"))
    if (a.willing_to_go_down_one_class) opts.push(t("willing_to_go_down_one_class"))
    if (opts.length === 0) return ""
    const badges = opts.map(o => `<span class="badge badge-partner-extra">${escapeHtml(o)}</span>`).join("")
    return `<div class="mb-2"><span class="fw-semibold me-1">${escapeHtml(t("extra_info"))}:</span><div class="d-flex flex-wrap gap-1 mt-1">${badges}</div></div>`
  }

  _renderGallery(a) {
    const gallery = a.media.filter(m => m.url || m.embed_url)
    if (gallery.length === 0) return ""
    const hero = gallery[0]
    const thumbs = gallery.slice(1)
    const gid = `partner-announcement-gallery-${a.id}`
    let heroHtml = ""
    if (hero.media_type === "image" && hero.url) {
      heroHtml = `<a href="${escapeHtml(hero.url)}" data-rel="lightcase:${gid}" ${hero.image_width ? `data-lc-width="${hero.image_width}"` : ""} ${hero.image_height ? `data-lc-height="${hero.image_height}"` : ""}><img src="${escapeHtml(hero.medium_url || hero.url)}" class="img-fluid rounded" style="object-fit: cover; width: 100%; height: 100%;"></a>`
    } else if (hero.media_type === "video") {
      heroHtml = `<a href="${escapeHtml(hero.url || hero.embed_url)}" data-rel="lightcase:${gid}" class="partner-announcement-gallery__hero-video bg-dark d-flex align-items-center justify-content-center"><i class="fa-solid fa-play-circle fa-4x text-white"></i></a>`
    }
    let thumbsHtml = ""
    if (thumbs.length > 0) {
      thumbsHtml = `<div class="partner-announcement-gallery__thumbs">${thumbs.map(m => {
        if (m.media_type === "image" && m.url) return `<a href="${escapeHtml(m.url)}" data-rel="lightcase:${gid}" class="partner-announcement-gallery__thumb"><img src="${escapeHtml(m.thumb_url || m.url)}" class="img-fluid rounded w-100 h-100" style="object-fit: cover;"></a>`
        if (m.media_type === "video") return `<a href="${escapeHtml(m.url || m.embed_url)}" data-rel="lightcase:${gid}" class="partner-announcement-gallery__thumb partner-announcement-gallery__thumb--video rounded"><i class="fa-solid fa-play-circle fa-2x text-white"></i></a>`
        return ""
      }).join("")}</div>`
    }
    return `<div class="col-md-5"><div class="partner-announcement-gallery p-2"><div class="partner-announcement-gallery__hero mb-2">${heroHtml}</div>${thumbsHtml}</div></div>`
  }

  _locationHtml(loc) {
    if (!loc || (!loc.city && !loc.voivodeship)) return ""
    const parts = [loc.city, loc.voivodeship].filter(Boolean).join(", ")
    const country = loc.country && loc.country !== "PL" ? ` (${loc.country})` : ""
    return `<p><i class="fa-solid fa-location-dot me-2"></i>${escapeHtml(parts + country)}</p>`
  }

  _datesHtml(a) {
    if (!a.published_at && !a.expires_at && !a.updated_at) return ""
    let html = '<div class="mt-2 pt-2 border-top">'
    if (a.published_at) html += `<p class="mb-1 small text-muted">${escapeHtml(t("published_at", { default: "Data publikacji" }))}: ${formatDate(a.published_at)}</p>`
    if (a.expires_at) html += `<p class="mb-1 small text-muted">${escapeHtml(t("expires_at", { default: "Data wygaśnięcia" }))}: ${formatDate(a.expires_at)}</p>`
    if (a.updated_at && a.updated_at !== a.published_at) html += `<p class="mb-1 small text-muted">${escapeHtml(t("updated_at", { default: "Ostatnia modyfikacja" }))}: ${formatDate(a.updated_at)}</p>`
    html += `<p class="mb-0 small text-muted"><i class="fa-solid fa-eye me-1"></i>${a.views_count || 0}</p>`
    html += "</div>"
    return html
  }

  _contactHtml(contact) {
    if (!contact) return `<p class="text-muted">${escapeHtml(t("no_contact", { default: "Brak danych kontaktowych" }))}</p>`
    let html = ""
    if (contact.email) html += `<p class="mb-1"><a href="mailto:${escapeHtml(contact.email)}" class="text-decoration-none">${escapeHtml(contact.email)}</a></p>`
    if (contact.phone) html += `<p class="mb-1"><a href="tel:${escapeHtml(contact.phone)}" class="text-decoration-none">${escapeHtml(contact.phone)}</a></p>`
    if (contact.parent_phone) html += `<p class="mb-1 small">Telefon rodzica: <a href="tel:${escapeHtml(contact.parent_phone)}" class="text-decoration-none">${escapeHtml(contact.parent_phone)}</a></p>`
    if (contact.coach_phone) html += `<p class="mb-0 small">Telefon trenera: <a href="tel:${escapeHtml(contact.coach_phone)}" class="text-decoration-none">${escapeHtml(contact.coach_phone)}</a></p>`
    if (!html) html = `<p class="text-muted">${escapeHtml(t("no_contact", { default: "Brak danych kontaktowych" }))}</p>`
    return html
  }

  _videoLinkHtml(a) {
    if (!a.video_url || (a.media && a.media.some(m => m.media_type === "video"))) return ""
    return `<div class="mt-3 pt-3 border-top"><h6 class="mb-2">${escapeHtml(t("video_url"))}</h6><a href="${escapeHtml(a.video_url)}" target="_blank" rel="noopener" class="text-decoration-none">${escapeHtml(a.video_url)}</a></div>`
  }

  _simpleFormat(text) {
    return escapeHtml(text).replace(/\n\n/g, "</p><p>").replace(/\n/g, "<br>")
  }

  _reportLinkHtml() {
    return `<div class="card mt-4"><div class="card-body"><a class="forum-post-control-link text-muted" href="#" data-bs-toggle="modal" data-bs-target="#partnerAnnouncementReportModal"><i class="fa-solid fa-flag me-1"></i>${escapeHtml(t("report_link", { default: "Zgłoś nadużycie" }))}</a></div></div>`
  }

  _shareModalHtml() {
    return `<div class="modal fade" id="partnerAnnouncementShareModal" tabindex="-1" aria-hidden="true"><div class="modal-dialog modal-dialog-centered"><div class="modal-content">
      <div class="modal-header"><h5 class="modal-title">${escapeHtml(t("share_link", { default: "Udostępnij link" }))}</h5><button class="btn-close" type="button" data-bs-dismiss="modal"></button></div>
      <div class="modal-body"><div class="input-group"><input class="form-control" id="paShareUrlInput" type="text" readonly value="${escapeHtml(window.location.href)}"><button class="btn btn-outline-secondary" id="paCopyShareUrl" type="button"><i class="fa-solid fa-copy me-1"></i>${escapeHtml(t("copy_link", { default: "Kopiuj" }))}</button></div>
      <div class="small text-success mt-2 d-none" id="paCopySuccess"><i class="fa-solid fa-check me-1"></i>${escapeHtml(t("link_copied", { default: "Skopiowano!" }))}</div></div>
    </div></div></div>`
  }

  _reportModalHtml() {
    const reasons = ["spam", "inappropriate", "harassment", "misinformation", "other"]
    const opts = reasons.map(r => `<option value="${r}">${escapeHtml(r)}</option>`).join("")
    return `<div class="modal fade" id="partnerAnnouncementReportModal" tabindex="-1" aria-hidden="true"><div class="modal-dialog modal-dialog-centered"><div class="modal-content">
      <div class="modal-header"><h5 class="modal-title">${escapeHtml(t("report_title", { default: "Zgłoś ogłoszenie" }))}</h5><button class="btn-close" type="button" data-bs-dismiss="modal"></button></div>
      <div class="modal-body">
        <div class="mb-3"><label class="form-label">${escapeHtml(t("report_reason", { default: "Powód" }))}</label><select class="form-select" id="pa_report_reason"><option value=""></option>${opts}</select></div>
        <div class="mb-3"><label class="form-label">${escapeHtml(t("report_description", { default: "Opis" }))}</label><textarea class="form-control" id="pa_report_description" rows="4"></textarea></div>
      </div>
      <div class="modal-footer"><button class="btn btn-secondary" type="button" data-bs-dismiss="modal">${escapeHtml(t("common.cancel", { default: "Anuluj" }))}</button><button class="default-btn reverse" type="button" data-action="click->partner-announcement-show#submitReport">${escapeHtml(t("report_submit", { default: "Wyślij" }))}</button></div>
    </div></div></div>`
  }

  openShareModal(e) {
    e.preventDefault()
    const modal = this.element.querySelector("#partnerAnnouncementShareModal")
    if (modal && window.bootstrap) new window.bootstrap.Modal(modal).show()
  }

  _initShareModal() {
    const copyBtn = this.element.querySelector("#paCopyShareUrl")
    const input = this.element.querySelector("#paShareUrlInput")
    const success = this.element.querySelector("#paCopySuccess")
    if (copyBtn && input) {
      copyBtn.addEventListener("click", () => {
        navigator.clipboard.writeText(input.value).then(() => {
          success?.classList.remove("d-none")
          setTimeout(() => success?.classList.add("d-none"), 3000)
        })
      })
    }
  }

  _initLightcase() {
    document.dispatchEvent(new CustomEvent('dancemate:lightcase:init'))
  }

  async publish() {
    try { await apiPost(`/api/v1/partner_announcements/${this.announcementIdValue}/publish`); window.location.reload() }
    catch (e) { alert(e.message) }
  }

  async close() {
    try { await apiPost(`/api/v1/partner_announcements/${this.announcementIdValue}/close`); window.location.reload() }
    catch (e) { alert(e.message) }
  }

  async reopen() {
    try { await apiPost(`/api/v1/partner_announcements/${this.announcementIdValue}/renew`); window.location.reload() }
    catch (e) { alert(e.message) }
  }

  async submitReport() {
    const reason = this.element.querySelector("#pa_report_reason")?.value
    const description = this.element.querySelector("#pa_report_description")?.value
    if (!reason) return alert(t("report_reason_required", { default: "Wybierz powód zgłoszenia." }))
    try {
      await apiPost(`/api/v1/partner_announcements/${this.announcementIdValue}/report_abuse`, { partner_announcement_report: { reason, description } })
      const modal = this.element.querySelector("#partnerAnnouncementReportModal")
      if (modal && window.bootstrap) window.bootstrap.Modal.getInstance(modal)?.hide()
      alert(t("report_sent", { default: "Zgłoszenie wysłane." }))
    } catch (e) { alert(e.message) }
  }
}
